import {Inject, Injectable} from '@angular/core';

import {Observable, of} from 'rxjs';

import {EntryStore, EntryState, CachedEntry} from '@stores/entry.store';
import {PendingOperation} from '@stores/entry-store-automaton/operation-state-machine';
import {ErrorReportStore, ErrorReport} from '@stores/error-report.store';

import {Action} from '@actions/action';

import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {KeyUtilsPL2 as KU} from '@common/utils/dist/utils/key-utils-pl2.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';
import {authEntries} from '@utils/auth-utils';

import {ErrorReportCode} from '@constants/app.config';
import {
  EventAPIService,
  EventAPIServiceToken,
} from '@services/event-api.service';
import {MasterQueryInputBuilder} from '@services/events/query-events';
import {APIError} from '@utils/api-errors';
import {
  DialogRendererService,
  DialogType,
} from '@components/dialogs/dialog-renderer.service';

@Injectable()
export class ExpireEntriesAction implements Action<string[], any> {
  constructor(
    private entryStore: EntryStore,
    private errorReportStore: ErrorReportStore,
    private dialogRenderer: DialogRendererService,
    @Inject(EventAPIServiceToken) private eventApiService: EventAPIService,
  ) {}

  execute(pKs: string[]): Observable<any> {
    if (U.isEmpty(pKs)) {
      return of(void 0);
    }
    const state = this.entryStore.state();
    const error = APIError.buildError({});
    const failed: CachedEntry[] = pKs.map((pK) => {
      const ce = state[pK];
      if (!U.isEmpty(ce)) {
        if (!U.isEmpty(ce._err) && U.isEmpty(error.status)) {
          error.status = ce._err.status;
          error.message = ce._err.message ?? ce._err;
        }
        return ce;
      }
    });
    const tlePKs = KU.getParentTLEPKs(pKs);
    const tleCEs = this._getTLEs(tlePKs, state);
    const aCEs = this._getAuthEntries(tlePKs, state);
    const ignored = this._getAllIgnored(state);

    const debugState: ErrorReport = {
      code: error.status ?? ErrorReportCode.ExpiredEntries,
      message: error.message ?? 'Expired entries deleted from entry cache.',
      entries: [...failed, ...tleCEs, ...aCEs, ...ignored],
    };

    this.errorReportStore.addReport(debugState);

    this._showErrorDialog(tleCEs);
    this.entryStore.destroyEntries(pKs);
    const opts = {}; // ?
    return this.eventApiService.sendEvent(
      new MasterQueryInputBuilder({pK: tlePKs, aER: false, opts}),
    );
  }

  private _getTLEs(pKs: string[], state: EntryState): CachedEntry[] {
    const tlCEs: CachedEntry[] = [];
    pKs.forEach((pK) => {
      if (
        state[pK] &&
        !tlCEs.find((ce) => KU.stringFromKey(ce.e as PL2.EntryId) === pK)
      ) {
        tlCEs.push(state[pK]);
      }
    });
    return tlCEs;
  }

  private _getAuthEntries(pKs: string[], state: EntryState): CachedEntry[] {
    const aES: CachedEntry[] = [];
    pKs.forEach((pK) => {
      if (state[pK]) {
        const entry = state[pK].e as PL2.EntryId;
        authEntries(entry.mId, entry.sK, state)
          .map((ae) => ae as PL2.AuthIdxEntry)
          .forEach((ae) => {
            const aPK = KU.stringFromKey({mId: ae.mId, sK: ae.aSK});
            if (state[aPK]) {
              aES.push(state[aPK]);
            }
          });
      }
    });
    return aES;
  }

  private _getAllIgnored(state: EntryState): CachedEntry[] {
    const ignored: CachedEntry[] = [];
    Object.keys(state)
      .map((pK) => state[pK])
      .filter((ce) => ce.pO === PendingOperation.Ignore)
      .forEach((iCE) => ignored.push(iCE));
    return ignored;
  }

  private _showErrorDialog(tles: CachedEntry[]) {
    const tleList = !U.isEmpty(tles)
      ? tles.map((tle) => tle.e.n).join('\n')
      : '';
    this.dialogRenderer.render({
      type: DialogType.Notification,
      data: {
        title: 'expired-entries-notification.title',
        content: `expired-entries-notification.content`,
        interpolation: {
          content: {
            tles: tleList,
          },
        },
      },
    });
  }
}
